import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, CircularProgress, Alert,
  Chip, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow, MenuItem
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import api from '../api';
import { useSede } from '../contexto/Sede';
import { usePeriodo } from '../contexto/Periodo';
import { ancho, tablaAncha } from '../estilos/responsivo';
import { pesos } from './Tile';
import { BLOQUE, CONTENEDOR_TABLA, TITULO_BLOQUE } from '../estilos/tarjeta';

// Medidas de la skill (`estilos/tarjeta.js`). Las tablas tienen un ancho
// mínimo: se desplazan dentro del bloque, no la página entera (en computador
// el balance de dos columnas se salía por la derecha; ahora va en dos
// columnas solo desde 1536 px, donde caben las tablas).
const paperStyle = { ...BLOQUE, overflowX: 'auto' };

function CuentaFila({ cuenta, indent = 0 }) {
  const bold = cuenta.es_grupo || cuenta.es_total;
  return (
    <TableRow sx={{ bgcolor: cuenta.es_total ? '#f8fafc' : 'transparent' }}>
      <TableCell sx={{ pl: 2 + indent * 3, fontSize: 13, fontWeight: bold ? 700 : 400, color: '#1e293b', fontFamily: cuenta.codigo ? 'monospace' : 'inherit' }}>
        {cuenta.codigo || ''}
      </TableCell>
      <TableCell sx={{ fontSize: 13, fontWeight: bold ? 700 : 400, color: bold ? '#1e293b' : '#334155' }}>
        {cuenta.nombre}
      </TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontWeight: bold ? 700 : 400, color: '#1e293b' }}>
        {cuenta.saldo !== undefined ? pesos(cuenta.saldo) : ''}
      </TableCell>
    </TableRow>
  );
}

function BalanceGeneral() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fecha, setFecha] = useState('');

  const fetch = async () => {
    setLoading(true);
    try {
      const params = fecha ? `?fecha=${fecha}` : '';
      const res = await api.get(`/contabilidad/balance-general${params}`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
  if (!data) return <Typography color="error">Error al cargar</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField label="Fecha corte" type="date" size="small" value={fecha}
          onChange={e => setFecha(e.target.value)} InputLabelProps={{ shrink: true }} sx={ancho(180)} />
        <Button variant="outlined" onClick={fetch}
          sx={{ textTransform: 'none', borderColor: '#1e3a5f', color: '#1e3a5f', borderRadius: 1, fontWeight: 600 }}>
          Generar
        </Button>
        <Chip label={data.total_activos === data.total_pasivos_patrimonio ? 'Cuadrado' : 'Descuadrado'}
          sx={{
            fontWeight: 600, fontSize: 11,
            bgcolor: data.total_activos === data.total_pasivos_patrimonio ? '#d1fae5' : '#fee2e2',
            color: data.total_activos === data.total_pasivos_patrimonio ? '#059669' : '#dc2626',
          }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1 }}>
        {/* Activos */}
        <Paper elevation={0} sx={paperStyle}>
          <Typography sx={{ ...TITULO_BLOQUE, mb: 1 }}>
            <AccountBalanceIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom', color: '#2563eb' }} />
            ACTIVOS
          </Typography>

          {data.activos_corrientes?.length > 0 && (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#64748b', mb: 0.5, ml: 1 }}>Corrientes</Typography>
              <Table size="small" sx={tablaAncha(600)}>
                <TableBody>
                  {data.activos_corrientes.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>{c.codigo}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{c.nombre}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{pesos(c.saldo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {data.activos_no_corrientes?.length > 0 && (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#64748b', mb: 0.5, ml: 1, mt: 1.5 }}>No Corrientes</Typography>
              <Table size="small" sx={tablaAncha(600)}>
                <TableBody>
                  {data.activos_no_corrientes.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>{c.codigo}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{c.nombre}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{pesos(c.saldo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <Box sx={{ mt: 2, pt: 1.5, borderTop: '2px solid #1e3a5f', display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>TOTAL ACTIVOS</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#2563eb' }}>{pesos(data.total_activos)}</Typography>
          </Box>
        </Paper>

        {/* Pasivos + Patrimonio */}
        <Paper elevation={0} sx={paperStyle}>
          <Typography sx={{ ...TITULO_BLOQUE, mb: 1 }}>
            <AccountBalanceIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom', color: '#db2777' }} />
            PASIVOS Y PATRIMONIO
          </Typography>

          {data.pasivos_corrientes?.length > 0 && (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#64748b', mb: 0.5, ml: 1 }}>Pasivos Corrientes</Typography>
              <Table size="small" sx={tablaAncha(600)}>
                <TableBody>
                  {data.pasivos_corrientes.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>{c.codigo}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{c.nombre}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{pesos(c.saldo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {data.pasivos_no_corrientes?.length > 0 && (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#64748b', mb: 0.5, ml: 1, mt: 1.5 }}>Pasivos No Corrientes</Typography>
              <Table size="small" sx={tablaAncha(600)}>
                <TableBody>
                  {data.pasivos_no_corrientes.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>{c.codigo}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{c.nombre}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{pesos(c.saldo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#64748b' }}>Total Pasivos</Typography>
            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{pesos(data.total_pasivos)}</Typography>
          </Box>

          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#64748b', mb: 0.5, ml: 1, mt: 2 }}>Patrimonio</Typography>
          <Table size="small" sx={tablaAncha(600)}>
            <TableBody>
              {(data.patrimonio || []).map((c, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>{c.codigo}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{c.nombre}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{pesos(c.saldo)}</TableCell>
                </TableRow>
              ))}
              {data.resultado_ejercicio !== undefined && (
                <TableRow>
                  <TableCell sx={{ fontSize: 12, color: '#7c3aed' }}>*</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, color: '#7c3aed' }}>Resultado del Ejercicio</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{pesos(data.resultado_ejercicio)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#64748b' }}>Total Patrimonio</Typography>
            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{pesos(data.total_patrimonio)}</Typography>
          </Box>

          <Box sx={{ mt: 2, pt: 1.5, borderTop: '2px solid #1e3a5f', display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>TOTAL PASIVOS + PATRIMONIO</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#db2777' }}>{pesos(data.total_pasivos_patrimonio)}</Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function EstadoResultados() {
  const { params: paramsSede, sedeId, nombre: nombreSede } = useSede();
  const { periodo } = usePeriodo();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = new Date();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get('/contabilidad/estado-resultados',
        { params: { periodo, ...paramsSede } });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Recarga al cambiar de sede: el estado de resultados es por punto de
  // operación, así que cambiar de sede cambia el informe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetch(); }, [sedeId, periodo]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
  if (!data) return <Typography color="error">Error al cargar</Typography>;

  const sections = [
    { label: 'Ingresos Operacionales', items: data.ingresos_operacionales, subtotal: data.total_ingresos_operacionales, color: '#059669' },
    { label: 'Costos de Ventas', items: data.costos, subtotal: data.total_costos, color: '#ea580c' },
    { label: 'Gastos Operacionales', items: data.gastos_operacionales, subtotal: data.total_gastos_operacionales, color: '#dc2626' },
    { label: 'Ingresos No Operacionales', items: data.ingresos_no_operacionales, subtotal: data.total_ingresos_no_operacionales, color: '#059669' },
    { label: 'Gastos No Operacionales', items: data.gastos_no_operacionales, subtotal: data.total_gastos_no_operacionales, color: '#dc2626' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Button variant="outlined" onClick={fetch}
          sx={{ textTransform: 'none', borderColor: '#1e3a5f', color: '#1e3a5f', borderRadius: 1, fontWeight: 600 }}>
          Generar
        </Button>
      </Box>

      <Paper elevation={0} sx={paperStyle}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b', mb: 2, textAlign: 'center' }}>
          Estado de Resultados — {data.periodo}
        </Typography>

        <Table size="small" sx={tablaAncha(600)}>
          <TableBody>
            {sections.map((sec, si) => (
              <React.Fragment key={si}>
                {sec.items?.length > 0 && (
                  <>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b', pt: si > 0 ? 2 : 0.5, borderBottom: '1px solid #e2e8f0' }}>
                        {sec.label}
                      </TableCell>
                    </TableRow>
                    {sec.items.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontSize: 12, pl: 3, color: '#334155' }}>
                          <span style={{ fontFamily: 'monospace', color: '#64748b', marginRight: 8 }}>{c.codigo}</span>
                          {c.nombre}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{pesos(c.saldo)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: sec.color, pl: 2 }}>Subtotal {sec.label}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: sec.color }}>{pesos((sec.subtotal || 0))}</TableCell>
                    </TableRow>
                  </>
                )}
              </React.Fragment>
            ))}

            {/* Utilidad Bruta */}
            <TableRow sx={{ bgcolor: '#f0fdf4' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 13, color: '#059669', pt: 2 }}>Utilidad Bruta</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13, color: '#059669', pt: 2 }}>{pesos((data.utilidad_bruta || 0))}</TableCell>
            </TableRow>

            {/* Utilidad Operacional */}
            <TableRow sx={{ bgcolor: '#eff6ff' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 13, color: '#2563eb' }}>Utilidad Operacional</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13, color: '#2563eb' }}>{pesos((data.utilidad_operacional || 0))}</TableCell>
            </TableRow>

            {/* Resultado Neto */}
            <TableRow sx={{ bgcolor: data.resultado_neto >= 0 ? '#f0fdf4' : '#fef2f2' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 15, color: data.resultado_neto >= 0 ? '#059669' : '#dc2626', borderTop: '2px solid #1e293b', pt: 2 }}>
                RESULTADO NETO
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: data.resultado_neto >= 0 ? '#059669' : '#dc2626', borderTop: '2px solid #1e293b', pt: 2 }}>
                {pesos((data.resultado_neto || 0))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

function BalancePrueba() {
  const { params: paramsSede, sedeId, nombre: nombreSede } = useSede();
  const { periodo } = usePeriodo();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const params = periodo ? `?periodo=${periodo}` : '';
      const res = await api.get(`/contabilidad/balance-prueba${params}`,
        { params: paramsSede });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetch(); }, [sedeId, periodo]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
  if (!data) return <Typography color="error">Error al cargar</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Button variant="outlined" onClick={fetch}
          sx={{ textTransform: 'none', borderColor: '#1e3a5f', color: '#1e3a5f', borderRadius: 1, fontWeight: 600 }}>
          Generar
        </Button>
        <Chip label={data.cuadrado ? 'Cuadrado' : 'Descuadrado'}
          sx={{
            fontWeight: 600, fontSize: 11,
            bgcolor: data.cuadrado ? '#d1fae5' : '#fee2e2',
            color: data.cuadrado ? '#059669' : '#dc2626',
          }} />
      </Box>

      <Paper elevation={0} sx={paperStyle}>
        <Table size="small" sx={tablaAncha(600)}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b' }}>Codigo</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b' }}>Cuenta</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#2563eb' }}>Debito</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#059669' }}>Credito</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#64748b' }}>Saldo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.cuentas || []).map((c, i) => (
              <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{c.codigo}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{c.nombre}</TableCell>
                <TableCell align="right" sx={{ fontSize: 12, color: c.debito > 0 ? '#1e293b' : '#cbd5e1' }}>
                  {c.debito > 0 ? pesos(c.debito) : '-'}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 12, color: c.credito > 0 ? '#1e293b' : '#cbd5e1' }}>
                  {c.credito > 0 ? pesos(c.credito) : '-'}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{pesos(c.saldo)}</TableCell>
              </TableRow>
            ))}
            {/* Totales */}
            <TableRow sx={{ bgcolor: '#f8fafc', borderTop: '2px solid #1e293b' }}>
              <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: 13 }}>TOTALES</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13, color: '#2563eb' }}>{pesos((data.total_debito || 0))}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13, color: '#059669' }}>{pesos((data.total_credito || 0))}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

function LibroMayor() {
  const [cuentas, setCuentas] = useState([]);
  const [selectedCuenta, setSelectedCuenta] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/contabilidad/cuentas').then(res => {
      setCuentas(res.data.filter(c => c.nivel >= 3 && c.activa));
    }).catch(console.error);
  }, []);

  const fetch = async () => {
    if (!selectedCuenta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ cuenta_id: selectedCuenta });
      if (periodo) params.append('periodo', periodo);
      const res = await api.get(`/contabilidad/libro-mayor?${params}`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField label="Cuenta" size="small" value={selectedCuenta} onChange={e => setSelectedCuenta(e.target.value)}
          select sx={{ minWidth: { xs: 0, sm: 280 }, width: { xs: '100%', sm: 'auto' } }}>
          {cuentas.map(c => (
            <MenuItem key={c.id} value={c.id}>{c.codigo} - {c.nombre}</MenuItem>
          ))}
        </TextField>
        <TextField label="Periodo" size="small" value={periodo} onChange={e => setPeriodo(e.target.value)}
          placeholder="2026-02" sx={ancho(140)} />
        <Button variant="outlined" onClick={fetch} disabled={!selectedCuenta}
          sx={{ textTransform: 'none', borderColor: '#1e3a5f', color: '#1e3a5f', borderRadius: 1, fontWeight: 600 }}>
          Consultar
        </Button>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>}

      {data && !loading && (
        <Paper elevation={0} sx={paperStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{data.cuenta_codigo} - {data.cuenta_nombre}</Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>Naturaleza: {data.naturaleza}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>Saldo actual</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#1e293b' }}>{pesos((data.saldo_final || 0))}</Typography>
            </Box>
          </Box>

          <Table size="small" sx={tablaAncha(600)}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b' }}>Asiento</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b' }}>Descripcion</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#2563eb' }}>Debito</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#059669' }}>Credito</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#64748b' }}>Saldo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.movimientos || []).map((m, i) => (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell sx={{ fontSize: 12 }}>{m.fecha}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>#{m.asiento_numero}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#334155' }}>{m.descripcion}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: m.debito > 0 ? '#1e293b' : '#cbd5e1' }}>
                    {m.debito > 0 ? pesos(m.debito) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: m.credito > 0 ? '#1e293b' : '#cbd5e1' }}>
                    {m.credito > 0 ? pesos(m.credito) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{pesos(m.saldo_acumulado)}</TableCell>
                </TableRow>
              ))}
              {(!data.movimientos || data.movimientos.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: '#94a3b8' }}>
                    Sin movimientos para esta cuenta
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {!data && !loading && (
        <Paper elevation={0} sx={{ ...paperStyle, textAlign: 'center', py: 5 }}>
          <MenuBookIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
          <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>Selecciona una cuenta</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>Elige una cuenta para ver sus movimientos detallados</Typography>
        </Paper>
      )}
    </Box>
  );
}

export default function ReportesFinancieros() {
  const [tab, setTab] = useState(0);

  const tabs = [
    { label: 'Balance General', icon: <AccountBalanceIcon /> },
    { label: 'Estado de Resultados', icon: <TrendingUpIcon /> },
    { label: 'Balance de Prueba', icon: <ListAltIcon /> },
    { label: 'Libro Mayor', icon: <MenuBookIcon /> },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ color: '#64748b', fontSize: 13 }}>Estados financieros NIIF para PYMES</Typography>
      </Box>

      <Paper elevation={0} sx={{ ...CONTENEDOR_TABLA, mb: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{
            px: 1,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 48 },
            '& .Mui-selected': { color: '#1e3a5f' },
            '& .MuiTabs-indicator': { backgroundColor: '#1e3a5f' },
          }}>
          {tabs.map((t, i) => (
            <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />
          ))}
        </Tabs>
      </Paper>

      {tab === 0 && <BalanceGeneral />}
      {tab === 1 && <EstadoResultados />}
      {tab === 2 && <BalancePrueba />}
      {tab === 3 && <LibroMayor />}
    </Box>
  );
}
